import { Flex } from '@/components/Flex';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Price } from '@/components/Price';
import { Typography } from '@/components/Typography';
import type { TaxedMoney } from '@/graphql/saleor/api';
import type {
  AllSelectedProducts,
  Product,
} from '@/hooks/useAllSelectedProductsState';
import type {
  StrapAddToBagBuckle,
  StrapAddToBagTool,
} from '@/hooks/useBucklesAndTools';
import { getBoolValue } from '@/lib/attributes';
import type { ProductTypeOptions } from '@/lib/product';
import { generateDataTestId } from '@/lib/tests';
import type { SetState } from '@/lib/types';
import { px2rem } from '@/theme';
import type { StrapAddToBagStrap } from '@/views/ShoppingBagView/components/ShoppingBagStrapModal';

import * as M from './messages';
import * as S from './ProductRow.styles';

interface CommonProductRowProps {
  data: StrapAddToBagBuckle | StrapAddToBagTool | StrapAddToBagStrap;
  productType: ProductTypeOptions;
  selectedSku: null | string;
}

interface ProductRowClickable extends CommonProductRowProps {
  isClickable: true;
  isDeselectable?: boolean;
  setAllSelectedProducts: SetState<AllSelectedProducts>;
  setSelectedSku: SetState<null | string>;
}

interface ProductRowNotClickable extends CommonProductRowProps {
  isClickable: false;
  isDeselectable?: never;
  setAllSelectedProducts?: never;
  setSelectedSku?: never;
}

export type ProductRowProps = ProductRowClickable | ProductRowNotClickable;

export const ProductRow = ({
  data,
  isClickable,
  isDeselectable = true,
  productType,
  selectedSku,
  setAllSelectedProducts,
  setSelectedSku,
}: ProductRowProps) => {
  const { id, pricing, product, sku } = data;
  const productPrice = pricing?.price;
  const selected = selectedSku === sku;
  const hasIntegratedBuckle =
    productType === 'strap' &&
    getBoolValue((data as StrapAddToBagStrap).product.integratedBuckle);

  const handleClick = (id: string, productPrice: TaxedMoney, sku: string) => {
    if (selectedSku === sku) {
      if (!isDeselectable) {
        return;
      }

      setSelectedSku?.(null);
      setAllSelectedProducts?.(
        (curr) =>
          ({
            products: curr.products.filter((product) => product.id !== id),
            totalPrice: {
              ...curr.totalPrice,
              gross: {
                ...curr.totalPrice.gross,
                amount:
                  curr.totalPrice.gross.amount - productPrice.gross.amount,
              },
              net: {
                ...curr.totalPrice.net,
                amount: curr.totalPrice.net.amount - productPrice.net.amount,
              },
            },
          } satisfies AllSelectedProducts),
      );

      return;
    }

    if (selectedSku) {
      setSelectedSku?.(sku);
      setAllSelectedProducts?.((curr) => {
        const previouslySelectedProduct = curr.products.find(
          (product) => product.productTypeSlug === productType,
        ) as Product;

        const productsWithoutPreviouslySelected = curr.products.filter(
          (product) => product.productTypeSlug !== productType,
        );

        return {
          products: [
            ...productsWithoutPreviouslySelected,
            {
              id,
              sku,
              price: productPrice,
              productTypeSlug: productType,
            },
          ],
          totalPrice: {
            ...curr.totalPrice,
            gross: {
              ...curr.totalPrice.gross,
              amount:
                curr.totalPrice.gross.amount -
                previouslySelectedProduct?.price?.gross?.amount +
                productPrice?.gross.amount,
            },
            net: {
              ...curr.totalPrice.net,
              amount:
                curr.totalPrice.net.amount -
                previouslySelectedProduct?.price?.net?.amount +
                productPrice.net.amount,
            },
          },
        } satisfies AllSelectedProducts;
      });

      return;
    }

    setSelectedSku?.(sku);
    setAllSelectedProducts?.(
      (curr) =>
        ({
          products: [
            ...curr.products,
            {
              id,
              sku,
              price: productPrice,
              productTypeSlug: productType,
            },
          ],
          totalPrice: {
            ...curr.totalPrice,
            currency: productPrice.currency,
            gross: {
              ...curr.totalPrice.gross,
              currency: productPrice.gross.currency,
              amount: curr.totalPrice.gross.amount + productPrice.gross.amount,
            },
            net: {
              ...curr.totalPrice.net,
              currency: productPrice.net.currency,
              amount: curr.totalPrice.net.amount + productPrice.net.amount,
            },
            tax: {
              ...curr.totalPrice.tax,
              currency: productPrice.tax.currency,
              amount: curr.totalPrice.tax.amount + productPrice.tax.amount,
            },
          },
        } satisfies AllSelectedProducts),
    );
  };

  return (
    <S.Wrapper
      $isClickable={isClickable}
      $selected={selected}
      data-test-id='ProductRowWrapper'
      onClick={() =>
        isClickable && productPrice && productPrice.gross.amount >= 0 && sku
          ? handleClick(id, productPrice, sku)
          : undefined
      }
      {...generateDataTestId('productRow', 'wrapper')}
    >
      <S.ImageWrapper>
        {product.thumbnail ? (
          <S.ProductImage
            alt={product.thumbnail.alt!}
            data-test-id='ProductRowImage'
            fill
            sizes='30vw'
            src={product.thumbnail.url}
          />
        ) : (
          <ImagePlaceholder
            data-test-id='ProductRowImagePlaceholder'
            productType={
              product.productType.name.toLowerCase() as ProductTypeOptions
            }
          />
        )}
      </S.ImageWrapper>
      <Flex
        alignItems='flex-start'
        flexDirection='column'
        justifyContent='center'
      >
        <Typography
          color='secondary'
          data-test-id='ProductRowName'
          fontFamily='italianPlate'
          scale='p1'
        >
          {product.name}
        </Typography>
        {productType !== 'tool' && (
          <Typography
            color='secondaryGrey'
            data-test-id='ProductRowSku'
            scale='p3'
            style={{ marginTop: px2rem(2) }}
          >
            {sku}
          </Typography>
        )}
        {productType === 'strap' && !hasIntegratedBuckle && (
          <Typography
            color='secondaryGrey'
            data-test-id='ProductRowBuckleNotIncluded'
            scale='p3'
            style={{ marginTop: px2rem(2) }}
          >
            {M.BuckleNotIncluded}
          </Typography>
        )}
        <Typography
          as='div'
          color='secondary'
          data-test-id='ProductRowPrice'
          marginTop={1}
          scale='p1'
        >
          <Price price={pricing?.price?.gross} size='inherited' />
        </Typography>
      </Flex>
    </S.Wrapper>
  );
};
